import { refDebounced, useTimeoutFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { ExistingPublicProfile } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, watch } from "vue";

type PublicProfilesResponse = {
    items: ExistingPublicProfile[];
    total: number;
};

export type PublicProfileSearchSource = "chat" | "my_people";

type PublicProfileSearchOptions = {
    initialSearchTerm?: string;
    source: PublicProfileSearchSource;
};

const PAGE_SIZE = 20;

export function usePublicProfileSearch({ initialSearchTerm = "", source }: PublicProfileSearchOptions) {
    const query = ref(initialSearchTerm);
    const debouncedQuery = refDebounced(query, 300);
    // The query actually sent to the backend: `query` is the live input,
    // `emittedQuery` is the last settled value — their difference means "still
    // debouncing", which `isDebouncing` exposes.
    const emittedQuery = ref(initialSearchTerm);
    const offset = ref(0);
    const searchUrl = computed(() => `/api/accounts/profile/list/?q=${encodeURIComponent(emittedQuery.value)}&offset=${offset.value}&limit=${PAGE_SIZE}`);

    // Search is rate-limited to one request per second per user; a residual 429
    // (e.g. the same user in another tab) must not clobber the results already
    // shown, so the error body never replaces `data`.
    const { data, error, isFetching, statusCode, execute, onFetchResponse, onFetchError } = useFetch(searchUrl, {
        immediate: !!initialSearchTerm.trim(),
        refetch: true,
        updateDataOnError: false,
        // A blank query never shows results, so don't spend the rate limit on it or keep an unfiltered list around.
        beforeFetch: ({ cancel }) => {
            if (!emittedQuery.value.trim()) {
                cancel();
            }
        },
    })
        .get()
        .json<PublicProfilesResponse>();

    // useFetch replaces `data` on every request, so pages have to be kept here to survive the next fetch.
    const accumulatedProfiles = ref<ExistingPublicProfile[]>([]);

    const hasQuery = computed(() => !!query.value.trim());
    const isDebouncing = computed(() => query.value !== emittedQuery.value);
    // A search that lands inside the rate-limit window (for example right after a people-list search,
    // which shares the limit) is retried once after the server's one-second Retry-After rather than
    // surfacing as a failure.
    let retriedUrl = "";
    const { isPending: isRetrying, start: scheduleRetry } = useTimeoutFn(
        (url: string) => {
            if (url === searchUrl.value) {
                void execute();
            }
        },
        1000,
        { immediate: false },
    );
    onFetchError(() => {
        if (statusCode.value === 429 && retriedUrl !== searchUrl.value) {
            retriedUrl = searchUrl.value;
            scheduleRetry(searchUrl.value);
        }
    });
    const visibleError = computed(() => (isRetrying.value ? null : error.value));
    // A retried page load keeps the loaded list on screen; only a retried first page is a full search.
    const isLoadingMore = computed(() => offset.value > 0 && (isFetching.value || isRetrying.value));
    const isSearching = computed(() => isDebouncing.value || (offset.value === 0 && (isFetching.value || isRetrying.value)));
    // Keep the last good page on a transient error (e.g. a residual 429) rather
    // than blanking the results the user already has.
    const profiles = computed(() => (!hasQuery.value || isSearching.value ? [] : accumulatedProfiles.value));
    const total = computed(() => data.value?.total ?? 0);
    const hasMore = computed(() => hasQuery.value && accumulatedProfiles.value.length < total.value);
    const hasSearched = computed(() => data.value !== null && !visibleError.value);
    const hasNoMatch = computed(() => hasSearched.value && !isSearching.value && !!query.value.trim() && profiles.value.length === 0);

    const loadMore = () => {
        if (isFetching.value || isRetrying.value || isDebouncing.value || !hasMore.value) {
            return;
        }
        offset.value += PAGE_SIZE;
    };

    // Synchronous so the offset is back to 0 before `refetch` reacts to the new debounced term,
    // otherwise a new search would first request whichever page the previous one had scrolled to.
    // `accumulatedProfiles` is deliberately not cleared here — `isSearching` already hides it
    // while the request is pending, and keeping it means a failed request (e.g. a residual
    // rate-limit 429) leaves the previous results in place instead of emptying the list.
    watch(
        debouncedQuery,
        (value) => {
            offset.value = 0;
            emittedQuery.value = value;
            retriedUrl = "";
            if (!value.trim()) {
                accumulatedProfiles.value = [];
            }
        },
        { flush: "sync" },
    );

    onFetchResponse(() => {
        retriedUrl = "";
        const items = data.value?.items;
        if (!Array.isArray(items)) {
            return;
        }
        accumulatedProfiles.value = offset.value > 0 ? [...accumulatedProfiles.value, ...items] : items;
        void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_SEARCHED, { result_count: data.value?.total ?? 0, source });
    });

    watch(visibleError, (value) => {
        if (value) {
            logError(`Collaboration profile search failed: ${value}`);
        }
    });

    return { query, profiles, error: visibleError, isFetching: isSearching, hasNoMatch, total, hasMore, isLoadingMore, loadMore };
}
